import type { ComponentType } from 'react';
import type { GridCellParams, GridFilterOperator, ValueOptions } from '@mui/x-data-grid-pro';
import type { GroupedGridColDef } from './gridColumnTypes';
import type { EntityTagRendererProps } from './CellRenderers/EntityTagRenderer';
import type { SupportedTagEntities, Tag } from '../../../store/Tags';
import type { TagEntityRefresh } from '../Tagging/Tags';
import type { SupportedTagEntityTypes } from '../../../store/tagEntityTypes';

// populateColumnValueOptions, the filter input, the permission-set catalog and the api's grid-field mirrors all key on this id.
export const TAGS_FIELD = 'tags';

// Single-entity grids pass the constant; grids whose rows mix entities pass a resolver.
export type TagEntityType = SupportedTagEntityTypes | ((row: SupportedTagEntities) => SupportedTagEntityTypes);

export interface TagsColumnOptions {
  // Injected, not imported: a value import would half-initialize `tagsColumn` on the col-def ↔ store/User cycle.
  filterOperators: GridFilterOperator[];
  renderer: ComponentType<EntityTagRendererProps>;
  entityType: TagEntityType;
  // Defaults to the row's `id`; Orders rows overwrite that with the Salesforce one, so they resolve.
  entityId?: (row: SupportedTagEntities) => string;
  // The account the api binds a tag to; a grid that cannot name its rows' account omits it and keeps the selected-account default.
  entityAccountId?: (row: SupportedTagEntities) => string | undefined;
  refetch: TagEntityRefresh;
  columnGroup?: string;
  width?: number;
}

// Options arrive as Tag objects from the rows, and as plain names once flattened.
const tagOptionName = (option: ValueOptions): string => (option as Tag)?.name ?? String(option);

export const tagsColumn = ({
  filterOperators,
  renderer: TagCell,
  entityType,
  entityId,
  entityAccountId,
  refetch,
  columnGroup,
  width = 300
}: TagsColumnOptions): GroupedGridColDef => {
  // A resolver means the row id is the wrong key, so its result stands even when it comes back empty.
  const taggedId = (row: SupportedTagEntities): string => (entityId ? entityId(row) : (row as { id: string })?.id);

  return {
    field: TAGS_FIELD,
    headerName: 'Tags',
    sortable: false,
    width,
    filterable: true,
    type: 'singleSelect',
    valueOptions: [],
    valueGetter: (_value, row) => row?.tags?.map((tag: Tag) => tag.name) || [],
    getOptionValue: tagOptionName,
    getOptionLabel: tagOptionName,
    renderCell: (params: GridCellParams) => {
      const rowEntityId = taggedId(params.row);

      return (
        <TagCell
          entity={params.row}
          entityId={rowEntityId}
          entityAccountId={entityAccountId?.(params.row)}
          entityType={typeof entityType === 'function' ? entityType(params.row) : entityType}
          fetchFn={refetch}
          // An id the row never resolved cannot be written to, so the chips render without the editor that would try.
          readOnly={!rowEntityId}
        />
      );
    },
    cellClassName: (params: GridCellParams) => (taggedId(params.row) ? 'mui-grid-cell-interactive' : ''),
    filterOperators,
    ...(columnGroup ? { columnGroup } : {})
  };
};
